/* eslint-disable @typescript-eslint/no-explicit-any */
import { getDataValue, setDataValue } from '@juvare/json-data-util';
import { mapToString, mapToStringOrUndefined } from '@juvare/widget-ui/Utils';
import { walkWidgetConfigTree } from '../walkWidgetConfigTree';
export function buildGeneralTab(props, params) {
    const { dataObjWithCount, allFailingPaths, parentDataPath } = collectDataPathsWithCount(props);
    const currentKey = props.config.key;
    const children = [];
    if ((params === null || params === void 0 ? void 0 : params.key) || (params === null || params === void 0 ? void 0 : params.keyOptional)) {
        children.push({
            type: 'form.panel',
            key: '!keyPanel',
            children: [
                {
                    type: 'form.text',
                    key: 'key',
                    label: 'Key',
                    emptyAsNull: true,
                    validate: {
                        required: params.key,
                        custom: (value) => validateKey(value),
                    },
                },
                {
                    type: 'form.button',
                    label: 'Generate Key From Label',
                    event: {
                        onClick: (handler) => {
                            var _a, _b, _c;
                            const label = (_b = (_a = handler.up().up().down('label')) === null || _a === void 0 ? void 0 : _a.getData(mapToString)) !== null && _b !== void 0 ? _b : '';
                            const key = makeCamelCase(label);
                            (_c = handler.up().down('key')) === null || _c === void 0 ? void 0 : _c.setData(key);
                        },
                    },
                    style: {
                        paddingTop: 8,
                        noWrap: true,
                    },
                },
            ],
            style: {
                layout: 'row',
            },
        });
        children.push({
            type: 'form.content',
            text: 'This key is already used by some other component. ' +
                'That might indicate an error in form configuration. ' +
                'You need to make sure that no 2 or more components with the same key are visible at the same time.',
            style: {
                alert: 'warning',
            },
            hidden: {
                custom: (handler) => {
                    var _a, _b, _c, _d;
                    const key = (_b = (_a = handler.up().down('!keyPanel')) === null || _a === void 0 ? void 0 : _a.down('key')) === null || _b === void 0 ? void 0 : _b.getData(mapToStringOrUndefined);
                    if (!key) {
                        return true;
                    }
                    const keyDataPath = mapKeyToDataPath(key !== null && key !== void 0 ? key : '');
                    const fullDataPath = parentDataPath + (parentDataPath ? '.' : '') + keyDataPath;
                    const duplicatesCount = getDataValue(dataObjWithCount, fullDataPath);
                    const hasChildren = Boolean((_d = (_c = props.widget).childConfigs) === null || _d === void 0 ? void 0 : _d.call(_c, props.config).length);
                    const dataPathExists = typeof duplicatesCount === 'number' && duplicatesCount > 0;
                    if (dataPathExists) {
                        return false;
                    }
                    const isKeyOverridingObject = typeof duplicatesCount === 'object' && !hasChildren;
                    if (isKeyOverridingObject) {
                        return false;
                    }
                    const isKeyInFailingPaths = allFailingPaths.has(fullDataPath);
                    if (isKeyInFailingPaths) {
                        return false;
                    }
                    return isDataPathValidForAddingCount(dataObjWithCount, fullDataPath);
                },
            },
        });
        if (!props.config['_internal_new_']) {
            children.push({
                type: 'form.content',
                text: 'You are changing the key for an existing component. ' +
                    'This will change the way data is stored for new and updated records.',
                style: {
                    alert: 'warning',
                },
                hidden: {
                    custom: (handler) => {
                        var _a, _b;
                        const key = (_b = (_a = handler.up().down('!keyPanel')) === null || _a === void 0 ? void 0 : _a.down('key')) === null || _b === void 0 ? void 0 : _b.getData(mapToStringOrUndefined);
                        return key === currentKey;
                    },
                },
            });
        }
        children.push({
            type: 'form.content',
            text: 'The key should use camel case naming convention. ' +
                'Click the Generate Key From Label button for an example how the key should look like.',
            style: {
                alert: 'warning',
            },
            hidden: {
                custom: (handler) => {
                    var _a, _b;
                    const key = (_b = (_a = handler.up().down('!keyPanel')) === null || _a === void 0 ? void 0 : _a.down('key')) === null || _b === void 0 ? void 0 : _b.getData(mapToString);
                    return key.startsWith('!') ? isKeyCamelCase(key.slice(1)) : isKeyCamelCase(key);
                },
            },
        });
    }
    if (params === null || params === void 0 ? void 0 : params.label) {
        children.push({
            type: 'form.text',
            key: 'label',
            label: 'Label',
        });
    }
    if (params === null || params === void 0 ? void 0 : params.labelOptional) {
        children.push({
            type: 'form.text',
            key: 'label',
            label: 'Label',
            emptyAsNull: true,
        });
    }
    if (params === null || params === void 0 ? void 0 : params.description) {
        children.push({
            type: 'form.text',
            key: 'description',
            label: 'Description',
            multiline: true,
            rows: 4,
            emptyAsNull: true,
        });
    }
    if (params === null || params === void 0 ? void 0 : params.ariaLabel) {
        children.push({
            type: 'form.text',
            key: 'ariaLabel',
            label: 'Aria Label',
            description: 'Invisible text label for screen readers, used when grouping multiple form controls.',
        });
    }
    if (params === null || params === void 0 ? void 0 : params.disabled) {
        children.push({
            type: 'form.checkbox',
            key: 'disabled',
            label: 'Disabled',
        });
    }
    if (params === null || params === void 0 ? void 0 : params.readOnly) {
        children.push({
            type: 'form.checkbox',
            key: 'readOnly',
            label: 'Read Only',
        });
    }
    if (params === null || params === void 0 ? void 0 : params.tooltip) {
        children.push({
            type: 'form.checkbox',
            key: 'tooltip.show',
            label: 'Show Tooltip',
        }, {
            type: 'form.panel',
            hidden: {
                when: '^tooltip.show',
                dataNotEq: true,
            },
            children: [
                {
                    label: 'Tooltip Content Type',
                    type: 'form.radio',
                    key: 'tooltip.contentType',
                    values: [
                        { value: 'text', label: 'Text' },
                        { value: 'html', label: 'HTML' },
                    ],
                },
                {
                    type: 'form.text',
                    key: 'tooltip.text',
                    label: 'Text',
                    multiline: true,
                    rows: 6,
                    hidden: {
                        when: '^tooltip.contentType',
                        dataNotEq: 'text',
                    },
                    emptyAsNull: true,
                },
                {
                    type: 'form.text',
                    key: 'tooltip.html',
                    label: 'HTML',
                    multiline: true,
                    rows: 6,
                    hidden: {
                        when: '^tooltip.contentType',
                        dataNotEq: 'html',
                    },
                    emptyAsNull: true,
                },
            ],
        });
    }
    if (params === null || params === void 0 ? void 0 : params.children) {
        children.push(...params.children);
    }
    if (params === null || params === void 0 ? void 0 : params.autoFocus) {
        children.push({
            type: 'form.checkbox',
            key: 'autoFocus',
            label: 'Autofocus',
        });
    }
    if (params === null || params === void 0 ? void 0 : params.appendRowIndexToLabel) {
        children.push({
            type: 'form.checkbox',
            key: 'appendRowIndexToLabel',
            label: 'Append Row Index To Label',
            tooltip: {
                text: 'Index is visible if current widget is used as a child in DataGrid.',
            },
        });
    }
    const onLoad = (data) => {
        var _a;
        if (params === null || params === void 0 ? void 0 : params.tooltip) {
            data.tooltip = Object.assign({ contentType: ((_a = data.tooltip) === null || _a === void 0 ? void 0 : _a.html) != null ? 'html' : 'text' }, data.tooltip);
        }
        if (params === null || params === void 0 ? void 0 : params.onLoad) {
            data = params.onLoad(data);
        }
        return data;
    };
    const onSubmit = (data) => {
        var _a, _b, _c, _d, _e;
        if (params === null || params === void 0 ? void 0 : params.tooltip) {
            if (((_a = data.tooltip) === null || _a === void 0 ? void 0 : _a.contentType) === 'text') {
                (_b = data.tooltip) === null || _b === void 0 ? true : delete _b.html;
            }
            else {
                (_c = data.tooltip) === null || _c === void 0 ? true : delete _c.text;
            }
            (_d = data.tooltip) === null || _d === void 0 ? true : delete _d.contentType;
            if (!((_e = data.tooltip) === null || _e === void 0 ? void 0 : _e.show)) {
                delete data.tooltip;
            }
        }
        if (!(params === null || params === void 0 ? void 0 : params.autoFocus)) {
            delete data.autoFocus;
        }
        if (params === null || params === void 0 ? void 0 : params.onSubmit) {
            data = params.onSubmit(data);
        }
        return data;
    };
    return { label: 'General', children, onLoad, onSubmit };
}
export function mapKeyToDataPath(key) {
    return key
        .split('.')
        .filter((key) => !key.startsWith('!'))
        .join('.');
}
export function isDataPathValidForAddingCount(obj, path) {
    const keys = path.split('.');
    let tempPath = '';
    for (const [index, key] of keys.entries()) {
        tempPath += tempPath.length > 0 ? `.${key}` : key;
        const value = getDataValue(obj, tempPath);
        if (index === keys.length - 1) {
            continue;
        }
        if (value && typeof value === 'number') {
            return false;
        }
    }
    return true;
}
export function addCountToObjectByPath(obj, path) {
    if (!isDataPathValidForAddingCount(obj, path)) {
        return false;
    }
    const value = getDataValue(obj, path);
    if (value && typeof value === 'object') {
        return false;
    }
    if (!value || typeof value === 'number') {
        setDataValue(obj, path, value ? value + 1 : 1);
        return true;
    }
    return false;
}
export function collectDataPathsWithCount(props) {
    const dataObjWithCount = {};
    const failingPaths = new Set();
    let parentDataPath = '';
    walkWidgetConfigTree(props.rootConfig, props.widgets, (config, widget, param) => {
        var _a;
        let fullKey = param;
        const hasChildren = Boolean((_a = widget.childConfigs) === null || _a === void 0 ? void 0 : _a.call(widget, config).length);
        const configKey = config.key;
        if (config === props.config) {
            parentDataPath = mapKeyToDataPath(fullKey);
        }
        if (!config.key) {
            return fullKey;
        }
        fullKey = fullKey.length ? `${fullKey}.${configKey}` : configKey;
        const dataPath = mapKeyToDataPath(fullKey);
        if (config !== props.config && !hasChildren) {
            const isSuccessfullyAdded = addCountToObjectByPath(dataObjWithCount, dataPath);
            if (!isSuccessfullyAdded) {
                failingPaths.add(dataPath);
            }
        }
        if (widget.childDynamicRows) {
            fullKey += (fullKey.length > 0 ? '.' : '') + '#';
        }
        return fullKey;
    }, '');
    const allFailingPaths = new Set();
    Array.from(failingPaths).forEach((failingPath) => {
        const keys = failingPath.split('.');
        let tempPath = '';
        keys.forEach((key) => {
            tempPath = tempPath.length ? `${tempPath}.${key}` : key;
            allFailingPaths.add(tempPath);
        });
    });
    return { dataObjWithCount, allFailingPaths, parentDataPath };
}
function validateKey(key) {
    if (key) {
        for (const part of key.split('.').filter((keyPart) => keyPart !== '' && !keyPart.startsWith('!'))) {
            if (!/^#\d+\+?$/.test(part) && !/^\w+$/.test(part)) {
                return `Invalid key, the part "${part}" is not a valid`;
            }
        }
    }
    return undefined;
}
function makeCamelCase(text) {
    return text
        .replace(/[^[a-zA-Z\d]+/g, '.')
        .split('.')
        .map((word, index) => {
        let part = word.toLowerCase();
        if (index > 0) {
            part = part.substring(0, 1).toUpperCase() + part.substring(1);
        }
        return part;
    })
        .join('');
}
function isKeyCamelCase(key) {
    if (key) {
        for (const keyPart of key.split('.')) {
            if (!/^[a-z][a-zA-Z\d]*$/.test(keyPart)) {
                return false;
            }
        }
    }
    return true;
}
